import React, { useState, useEffect, useRef } from 'react';
import Layout from '../components/Layout';
import { PageKey } from '../components/Layout';
import {
  getAdAccounts, getCustomerSettings, getPixelsBulk, searchLocation,
  getTemplates, createTemplate, updateTemplateApi, deleteTemplateApi,
  startNewCampaign, SSE_BASE, getPagesForAccount, getAdTexts, updateAdText,
  updateAccountSettings, getPushCampaigns, getCampaignStructure,
} from '../api';

// ─── Types ────────────────────────────────────────────────────────────────────

interface AdSpec {
  name: string; headline?: string; copy_text?: string; description?: string; cta?: string;
}

interface AdSetSpec {
  name: string;
  age_min?: number; age_max?: number; gender?: number; radius_km?: number | null;
  ads: AdSpec[];
}

interface Template {
  id: string; name: string; description: string;
  campaign_type: string; campaign_name_tpl: string; adset_name_tpl: string;
  copy_text: string; headline: string; duration_weeks: number; daily_budget: number;
  optimization_goal: string; has_form: boolean; form_template: string;
  source_campaign_id?: string;
  // Kampagnen-Setup (Werte gehen 1:1 an den Push-Runner)
  age_min?: number; age_max?: number; ads_count?: number;
  facebook_only?: boolean; use_placeholder_image?: boolean;
  cta_type?: string; destination?: string;
  // Optional: mehrere Anzeigengruppen mit eigenen Anzeigen/Texten.
  // Leer = eine Gruppe mit ads_count identischen Anzeigen.
  ad_sets?: AdSetSpec[];
  last_used?: string | null; created_at: string;
}

interface Account {
  id: string; name: string; active: boolean;
  business_name?: string; page_name?: string; location?: string;
}

interface LocationEntry {
  _id: string;
  key?: string; name?: string; region?: string;
  radius_km: number;
}

interface TargetConfig {
  locations: LocationEntry[];
  gender: number; pixel_id: string; page_id: string;
  budget_euro: number; impressum_url: string; website_url: string; start_date: string;
  display_url: string; wordpress_url: string; use_wordpress: boolean;
}

interface Pixel { id: string; name: string; is_active: boolean | null; }

interface Props {
  user: { email: string; name: string };
  onLogout: () => void;
  onNavigate: (page: PageKey) => void;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const VARS = [
  { key: '{ort}', label: 'Ort', color: '#007aff' },
  { key: '{datum}', label: 'Datum', color: '#34c759' },
  { key: '{radius}', label: 'Radius', color: '#ff9500' },
  { key: '{km}', label: 'KM', color: '#ff9500' },
  { key: '{name}', label: 'Name', color: '#af52de' },
];

const REQUIRED_FIELDS: Record<string, string> = {
  location_key: 'Ort', pixel_id: 'Pixel', page_id: 'Seite',
  impressum_url: 'Impressum-URL', soka_ad_url: 'Webseite',
};

const CAMPAIGN_TYPES = ['LEAD_GENERATION', 'LINK_CLICKS', 'BRAND_AWARENESS', 'VIDEO_VIEWS'];
const FORM_TEMPLATES = ['soka', 'standard', 'hormon'];
const CTA_TYPES = ['SIGN_UP', 'LEARN_MORE', 'GET_OFFER', 'BOOK_TRAVEL', 'CONTACT_US', 'SEE_DETAILS'];
const DESTINATIONS = [
  { value: 'ON_AD', label: 'Instantformular (in der Anzeige)' },
  { value: 'WEBSITE', label: 'Website / Landingpage' },
];
const STEPS = ['Template', 'Partner', 'Einstellungen', 'Zusammenfassung', 'Push'];

let _uidCtr = 0;
const uid = () => `loc_${++_uidCtr}_${Math.random().toString(36).slice(2, 6)}`;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function resolvedName(acc: Account): string {
  if (acc.business_name?.trim() && !/^\d+$/.test(acc.business_name.trim())) return acc.business_name.trim();
  if (acc.page_name?.trim()) return acc.page_name.trim();
  if (acc.name?.trim() && !/^\d+$/.test(acc.name.trim())) return acc.name.trim();
  if (acc.name?.trim()) return acc.name.trim();
  return acc.id?.replace('act_', '') || '';
}

function getPartnerStatus(settings?: any): { status: 'ok' | 'warn' | 'error'; missing: string[] } {
  if (!settings) return { status: 'error', missing: Object.values(REQUIRED_FIELDS) };
  const missing = Object.entries(REQUIRED_FIELDS).filter(([k]) => !settings[k]).map(([, l]) => l);
  return { status: !settings.location_key ? 'error' : missing.length > 0 ? 'warn' : 'ok', missing };
}

function formatDate(iso?: string | null): string {
  if (!iso) return 'Noch nie verwendet';
  const d = new Date(iso);
  return `Zuletzt: ${d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}`;
}

function todayISO(): string { return new Date().toISOString().slice(0, 10); }

// ─── Template Modal ───────────────────────────────────────────────────────────

function emptyForm() {
  return {
    name: '', description: '', campaign_type: 'LEAD_GENERATION',
    campaign_name_tpl: '', adset_name_tpl: '', copy_text: '', headline: '',
    duration_weeks: 6, daily_budget: 50, optimization_goal: 'LEAD',
    has_form: true, form_template: 'soka', source_campaign_id: '', last_used: null,
    age_min: 35, age_max: 65, ads_count: 6,
    facebook_only: false, use_placeholder_image: true,
    cta_type: 'SIGN_UP', destination: 'WEBSITE', ad_sets: [],
  };
}

// ─── Anzeigengruppen-Editor (im Template-Modal) ───────────────────────────────

function AdSetsEditor({ adSets, sourceCampaignId, baseName, onChange }: {
  adSets: AdSetSpec[]; sourceCampaignId: string; baseName: string;
  onChange: (next: AdSetSpec[]) => void;
}) {
  const [open, setOpen] = useState<Record<number, boolean>>({ 0: true });
  const [loading, setLoading] = useState(false);
  const [loadMsg, setLoadMsg] = useState('');

  const totalAds = adSets.reduce((n, g) => n + (g.ads?.length || 0), 0);

  const patchSet = (i: number, patch: Partial<AdSetSpec>) =>
    onChange(adSets.map((g, gi) => gi === i ? { ...g, ...patch } : g));
  const patchAd = (i: number, ai: number, patch: Partial<AdSpec>) =>
    patchSet(i, { ads: adSets[i].ads.map((a, x) => x === ai ? { ...a, ...patch } : a) });

  const addSet = () => {
    onChange([...adSets, {
      name: `${baseName || 'Anzeigengruppe'} ${adSets.length + 1}`,
      age_min: 35, age_max: 65, gender: 0, radius_km: null,
      ads: [{ name: 'Bild 1' }],
    }]);
    setOpen(o => ({ ...o, [adSets.length]: true }));
  };
  const removeSet = (i: number) => onChange(adSets.filter((_, gi) => gi !== i));
  const addAd = (i: number) =>
    patchSet(i, { ads: [...adSets[i].ads, { name: `Bild ${adSets[i].ads.length + 1}` }] });
  const dupeAd = (i: number, ai: number) => {
    const src = adSets[i].ads[ai];
    const next = [...adSets[i].ads];
    next.splice(ai + 1, 0, { ...src, name: `${src.name} Kopie` });
    patchSet(i, { ads: next });
  };
  const removeAd = (i: number, ai: number) =>
    patchSet(i, { ads: adSets[i].ads.filter((_, x) => x !== ai) });

  const loadFromSource = async () => {
    if (!sourceCampaignId.trim()) { setLoadMsg('Erst eine Quell-Kampagnen-ID eintragen'); return; }
    setLoading(true); setLoadMsg('');
    try {
      const r = await getCampaignStructure(sourceCampaignId.trim());
      const loaded: AdSetSpec[] = (r.data.ad_sets || []).map((g: any) => ({
        name: g.name || '', age_min: g.age_min, age_max: g.age_max,
        gender: g.gender ?? 0, radius_km: g.radius_km ?? null,
        ads: (g.ads || []).map((a: any) => ({
          name: a.name || '', headline: a.headline || '', copy_text: a.copy_text || '',
          description: a.description || '', cta: a.cta || 'SIGN_UP',
        })),
      }));
      if (loaded.length === 0) {
        setLoadMsg(r.data.error ? `Fehler: ${r.data.error}` : 'Keine Anzeigengruppen gefunden');
      } else {
        onChange(loaded);
        setOpen({ 0: true });
        const n = loaded.reduce((x, g) => x + g.ads.length, 0);
        setLoadMsg(`${loaded.length} Gruppen / ${n} Anzeigen geladen`
          + (r.data.source_location ? ` · „${r.data.source_location}" → {ort}` : ''));
      }
    } catch (e: any) {
      setLoadMsg(`Fehler: ${e?.message || 'unbekannt'}`);
    }
    setLoading(false);
  };

  const box = { border: '1px solid #e8e8ed', borderRadius: 10, padding: '10px 12px', background: '#fafafc' };
  const small = { ...ms.input, padding: '6px 8px', fontSize: 12 };

  return (
    <div style={ms.fieldGroup}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <label style={ms.label}>
          Anzeigengruppen {adSets.length > 0 && <span style={{ color: '#6e6e73', fontWeight: 400 }}>
            · {adSets.length} Gruppen / {totalAds} Anzeigen</span>}
        </label>
        <div style={{ display: 'flex', gap: 6 }}>
          <button type="button" onClick={loadFromSource} disabled={loading}
            style={{ fontSize: 11, fontWeight: 600, color: '#1a3a5c', background: '#fff', border: '1px solid #1a3a5c', borderRadius: 7, padding: '4px 10px', cursor: 'pointer' }}>
            {loading ? 'Lade…' : 'Aus Quell-Kampagne laden'}
          </button>
          <button type="button" onClick={addSet}
            style={{ fontSize: 11, fontWeight: 600, color: '#1a3a5c', background: '#f0f5fb', border: '1px solid #1a3a5c40', borderRadius: 7, padding: '4px 10px', cursor: 'pointer' }}>
            + Gruppe
          </button>
        </div>
      </div>
      {loadMsg && <div style={{ fontSize: 11, color: loadMsg.startsWith('Fehler') ? '#ff3b30' : '#34c759', marginTop: 3 }}>{loadMsg}</div>}

      {adSets.length === 0 ? (
        <div style={{ ...box, fontSize: 12, color: '#6e6e73', marginTop: 6 }}>
          Keine Gruppen definiert – der Push legt <b>eine</b> Anzeigengruppe mit identischen
          Anzeigen an (Anzahl = „Anzahl Anzeigen", Text = Anzeigentext oben).
          Für mehrere Gruppen mit eigenen Texten: Gruppe hinzufügen oder aus der
          Quell-Kampagne laden.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 6 }}>
          {adSets.map((g, i) => (
            <div key={i} style={box}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button type="button" onClick={() => setOpen(o => ({ ...o, [i]: !o[i] }))}
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#aeaeb2', fontSize: 11 }}>
                  {open[i] ? '▲' : '▼'}
                </button>
                <input style={{ ...small, flex: 1 }} value={g.name}
                  placeholder="Gruppenname, z.B. {ort} Batch || 35-65"
                  onChange={e => patchSet(i, { name: e.target.value })} />
                <span style={{ fontSize: 11, color: '#aeaeb2', whiteSpace: 'nowrap' as const }}>
                  {g.ads?.length || 0} Ads
                </span>
                <button type="button" onClick={() => removeSet(i)} title="Gruppe entfernen"
                  style={{ background: 'transparent', border: '1px solid #e8e8ed', borderRadius: 6, color: '#ff3b30', cursor: 'pointer', width: 24, height: 26 }}>−</button>
              </div>

              {open[i] && (
                <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' as const }}>
                    <div>
                      <div style={{ fontSize: 10, color: '#aeaeb2' }}>Alter von</div>
                      <input type="number" style={{ ...small, width: 70 }} value={g.age_min ?? 35}
                        onChange={e => patchSet(i, { age_min: Number(e.target.value) })} />
                    </div>
                    <div>
                      <div style={{ fontSize: 10, color: '#aeaeb2' }}>bis</div>
                      <input type="number" style={{ ...small, width: 70 }} value={g.age_max ?? 65}
                        onChange={e => patchSet(i, { age_max: Number(e.target.value) })} />
                    </div>
                    <div>
                      <div style={{ fontSize: 10, color: '#aeaeb2' }}>Radius (leer = Partner)</div>
                      <input type="number" style={{ ...small, width: 90 }} value={g.radius_km ?? ''}
                        onChange={e => patchSet(i, { radius_km: e.target.value === '' ? null : Number(e.target.value) })} />
                    </div>
                    <div>
                      <div style={{ fontSize: 10, color: '#aeaeb2' }}>Geschlecht</div>
                      <select style={{ ...small, width: 110 }} value={g.gender ?? 0}
                        onChange={e => patchSet(i, { gender: Number(e.target.value) })}>
                        <option value={0}>Alle</option><option value={1}>Männer</option><option value={2}>Frauen</option>
                      </select>
                    </div>
                  </div>

                  {(g.ads || []).map((ad, ai) => (
                    <div key={ai} style={{ border: '1px solid #e8e8ed', borderRadius: 8, padding: '8px 10px', background: '#fff' }}>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <input style={{ ...small, width: 130 }} value={ad.name}
                          placeholder="Anzeigenname" onChange={e => patchAd(i, ai, { name: e.target.value })} />
                        <input style={{ ...small, flex: 1 }} value={ad.headline || ''}
                          placeholder="Headline (leer = Template-Headline)"
                          onChange={e => patchAd(i, ai, { headline: e.target.value })} />
                        <select style={{ ...small, width: 120 }} value={ad.cta || 'SIGN_UP'}
                          onChange={e => patchAd(i, ai, { cta: e.target.value })}>
                          {CTA_TYPES.map(t => <option key={t}>{t}</option>)}
                        </select>
                        <button type="button" onClick={() => dupeAd(i, ai)} title="Anzeige duplizieren"
                          style={{ background: 'transparent', border: '1px solid #e8e8ed', borderRadius: 6, color: '#1a3a5c', cursor: 'pointer', width: 26, height: 26 }}>⧉</button>
                        <button type="button" onClick={() => removeAd(i, ai)} title="Anzeige entfernen"
                          style={{ background: 'transparent', border: '1px solid #e8e8ed', borderRadius: 6, color: '#ff3b30', cursor: 'pointer', width: 24, height: 26 }}>−</button>
                      </div>
                      <textarea style={{ ...small, width: '100%', boxSizing: 'border-box' as const, minHeight: 60, marginTop: 6, resize: 'vertical' as const }}
                        value={ad.copy_text || ''}
                        placeholder="Anzeigentext dieser Anzeige (leer = Anzeigentext des Templates)"
                        onChange={e => patchAd(i, ai, { copy_text: e.target.value })} />
                    </div>
                  ))}
                  <button type="button" onClick={() => addAd(i)}
                    style={{ alignSelf: 'flex-start' as const, fontSize: 11, fontWeight: 600, color: '#1a3a5c', background: '#f0f5fb', border: '1px solid #1a3a5c40', borderRadius: 7, padding: '4px 10px', cursor: 'pointer' }}>
                    + Anzeige
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TemplateModal({ template, onSave, onSaveAs, onClose }: {
  template?: Template;
  onSave: (data: any) => Promise<void>;
  onSaveAs?: (data: any) => Promise<void>;
  onClose: () => void;
}) {
  const [form, setForm] = useState<any>(template ? { ...template } : emptyForm());
  const [saving, setSaving] = useState(false);
  const [activeField, setActiveField] = useState('');
  const refs = useRef<Record<string, HTMLInputElement | HTMLTextAreaElement | null>>({});

  const set = (k: string, v: any) => setForm((f: any) => ({ ...f, [k]: v }));

  const insertVar = (v: string) => {
    const el = refs.current[activeField];
    if (!el) return;
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? start;
    const cur = form[activeField] || '';
    set(activeField, cur.slice(0, start) + v + cur.slice(end));
    setTimeout(() => {
      const el2 = refs.current[activeField];
      if (el2) { el2.focus(); el2.setSelectionRange(start + v.length, start + v.length); }
    }, 0);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  const handleSaveAs = async () => {
    if (!form.name.trim() || !onSaveAs) return;
    setSaving(true);
    await onSaveAs(form);
    setSaving(false);
  };

  const textField = (key: string, label: string, placeholder = '') => (
    <div style={ms.fieldGroup}>
      <label style={ms.label}>{label}</label>
      <input style={ms.input} value={form[key] || ''} placeholder={placeholder}
        onChange={e => set(key, e.target.value)}
        onFocus={() => setActiveField(key)}
        ref={el => { refs.current[key] = el; }} />
    </div>
  );

  return (
    <div style={ms.overlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={ms.modal}>
        <div style={ms.header}>
          <span style={ms.title}>{template ? 'Template bearbeiten' : 'Neues Template erstellen'}</span>
          <button onClick={onClose} style={ms.closeBtn}>✕</button>
        </div>

        <div style={ms.varBar}>
          <span style={{ fontSize: 11, color: '#aeaeb2', marginRight: 6 }}>Variable einfügen:</span>
          {VARS.map(v => (
            <button key={v.key} onClick={() => insertVar(v.key)} style={{
              ...ms.varChip, background: v.color + '18', color: v.color, border: `1px solid ${v.color}40`,
            }}>{v.key}</button>
          ))}
        </div>

        <div style={ms.body}>
          <div style={ms.row2}>
            {textField('name', 'Name *', 'z.B. SOKA 6 Wochen')}
            <div style={ms.fieldGroup}>
              <label style={ms.label}>Kampagnentyp</label>
              <select style={ms.input} value={form.campaign_type} onChange={e => set('campaign_type', e.target.value)}>
                {CAMPAIGN_TYPES.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
          </div>
          {textField('description', 'Beschreibung', 'Kurze Beschreibung des Templates')}
          {textField('campaign_name_tpl', 'Kampagnenname-Vorlage', '{datum} SOKA || 6 Wochen')}
          {textField('adset_name_tpl', 'AdSet-Name-Vorlage', '{datum} SOKA || cold traffic {radius}k')}
          {textField('headline', 'Headline', '6-Wochen-Programm in {ort}')}
          <div style={ms.fieldGroup}>
            <label style={ms.label}>Anzeigentext</label>
            <textarea style={{ ...ms.input, minHeight: 100, resize: 'vertical' as const }}
              value={form.copy_text || ''} placeholder="Text mit {ort}, {datum}, {radius} etc."
              onChange={e => set('copy_text', e.target.value)}
              onFocus={() => setActiveField('copy_text')}
              ref={el => { refs.current['copy_text'] = el; }} />
          </div>
          <div style={ms.row2}>
            <div style={ms.fieldGroup}>
              <label style={ms.label}>Laufzeit (Wochen)</label>
              <input type="number" style={ms.input} min={1} max={52} value={form.duration_weeks}
                onChange={e => set('duration_weeks', Number(e.target.value))} />
            </div>
            <div style={ms.fieldGroup}>
              <label style={ms.label}>Tagesbudget (€)</label>
              <input type="number" style={ms.input} min={1} value={form.daily_budget}
                onChange={e => set('daily_budget', Number(e.target.value))} />
            </div>
          </div>
          <div style={ms.row2}>
            <div style={ms.fieldGroup}>
              <label style={ms.label}>Alter von</label>
              <input type="number" style={ms.input} min={18} max={65} value={form.age_min ?? 35}
                onChange={e => set('age_min', Number(e.target.value))} />
            </div>
            <div style={ms.fieldGroup}>
              <label style={ms.label}>Alter bis</label>
              <input type="number" style={ms.input} min={18} max={65} value={form.age_max ?? 65}
                onChange={e => set('age_max', Number(e.target.value))} />
            </div>
          </div>
          <div style={ms.row2}>
            <div style={ms.fieldGroup}>
              <label style={ms.label}>Anzahl Anzeigen</label>
              <input type="number" style={ms.input} min={1} max={20} value={form.ads_count ?? 6}
                onChange={e => set('ads_count', Number(e.target.value))} />
              <div style={{ fontSize: 11, color: '#aeaeb2', marginTop: 3 }}>
                So viele identische Anzeigen werden angelegt – Motive tauschst du danach aus
              </div>
            </div>
            <div style={ms.fieldGroup}>
              <label style={ms.label}>Button (CTA)</label>
              <select style={ms.input} value={form.cta_type || 'SIGN_UP'} onChange={e => set('cta_type', e.target.value)}>
                {CTA_TYPES.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
          </div>
          <div style={ms.row2}>
            <div style={ms.fieldGroup}>
              <label style={ms.label}>Conversion-Ort</label>
              <select style={ms.input} value={form.destination || 'WEBSITE'} onChange={e => set('destination', e.target.value)}>
                {DESTINATIONS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
              </select>
            </div>
            <div style={ms.fieldGroup}>
              <label style={ms.label}>Platzierungen & Bilder</label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, cursor: 'pointer', fontSize: 13 }}>
                <input type="checkbox" checked={!!form.facebook_only} onChange={e => set('facebook_only', e.target.checked)} />
                Nur Facebook (kein Instagram)
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, cursor: 'pointer', fontSize: 13 }}>
                <input type="checkbox" checked={!!form.use_placeholder_image}
                  onChange={e => set('use_placeholder_image', e.target.checked)} />
                Graue Platzhalterbilder verwenden
              </label>
            </div>
          </div>
          <div style={ms.row2}>
            <div style={ms.fieldGroup}>
              <label style={ms.label}>Formular</label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, cursor: 'pointer', fontSize: 13 }}>
                <input type="checkbox" checked={form.has_form} onChange={e => set('has_form', e.target.checked)} />
                Leadformular erstellen
              </label>
            </div>
            {form.has_form && (
              <div style={ms.fieldGroup}>
                <label style={ms.label}>Formular-Vorlage</label>
                <select style={ms.input} value={form.form_template} onChange={e => set('form_template', e.target.value)}>
                  {FORM_TEMPLATES.map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
            )}
          </div>
          <div style={ms.fieldGroup}>
            <label style={ms.label}>Quell-Kampagnen-ID (für Ads)</label>
            <input style={ms.input} value={form.source_campaign_id || ''} placeholder="z.B. 120200000123456789"
              onChange={e => set('source_campaign_id', e.target.value)} />
            <div style={{ fontSize: 11, color: '#aeaeb2', marginTop: 3 }}>
              Meta Kampagnen-ID aus dem Werbeanzeigenmanager – Struktur und Texte lassen sich
              darüber unten laden
            </div>
          </div>

          <AdSetsEditor
            adSets={form.ad_sets || []}
            sourceCampaignId={form.source_campaign_id || ''}
            baseName={form.adset_name_tpl || form.name || ''}
            onChange={next => set('ad_sets', next)}
          />
        </div>

        <div style={ms.footer}>
          <button onClick={onClose} style={ms.cancelBtn}>Abbrechen</button>
          {onSaveAs && (
            <button onClick={handleSaveAs} disabled={!form.name.trim() || saving}
              style={{ ...ms.cancelBtn, border: '1px solid #1a3a5c', color: '#1a3a5c', opacity: !form.name.trim() ? 0.5 : 1 }}>
              Als neues Template speichern
            </button>
          )}
          <button onClick={handleSave} disabled={!form.name.trim() || saving}
            style={{ ...ms.saveBtn, opacity: !form.name.trim() ? 0.5 : 1 }}>
            {saving ? 'Speichern…' : 'Speichern'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Step 1: Templates ────────────────────────────────────────────────────────

function Step1Templates({ onSelect }: { onSelect: (t: Template) => void }) {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Template | undefined>();
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);

  useEffect(() => {
    getTemplates().then(r => { setTemplates(r.data.templates || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const handleSave = async (data: any) => {
    if (editing) {
      const r = await updateTemplateApi(editing.id, data);
      setTemplates(ts => ts.map(t => t.id === editing.id ? r.data.template : t));
    } else {
      const r = await createTemplate(data);
      setTemplates(ts => [...ts, r.data.template]);
    }
    setShowModal(false);
    setEditing(undefined);
  };

  const handleSaveAs = async (data: any) => {
    const { id: _id, created_at: _ca, last_used: _lu, ...rest } = data;
    const r = await createTemplate(rest);
    setTemplates(ts => [...ts, r.data.template]);
    setShowModal(false);
    setEditing(undefined);
  };

  const handleDuplicate = async (t: Template) => {
    const { id: _id, created_at: _ca, last_used: _lu, ...rest } = t;
    const r = await createTemplate({ ...rest, name: `Kopie von ${t.name}` });
    setTemplates(ts => [...ts, r.data.template]);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Template löschen?')) return;
    await deleteTemplateApi(id);
    setTemplates(ts => ts.filter(t => t.id !== id));
  };

  if (loading) return <div style={s.emptyState}>Lade Templates…</div>;

  return (
    <div>
      {menuOpenId && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 99 }} onClick={() => setMenuOpenId(null)} />
      )}

      <div style={s.stepHeader}>
        <div>
          <div style={s.stepTitle}>Template auswählen</div>
          <div style={s.stepSub}>Wähle ein bestehendes Template oder erstelle ein neues</div>
        </div>
        <button style={s.primaryBtn} onClick={() => { setEditing(undefined); setShowModal(true); }}>
          + Neues Template
        </button>
      </div>

      {templates.length === 0 ? (
        <div style={s.emptyState}>Noch keine Templates. Erstelle dein erstes!</div>
      ) : (
        <div style={s.tplGrid}>
          {templates.map(t => (
            <div key={t.id} style={s.tplCard}>
              <div style={s.tplTop}>
                <span style={s.tplName}>{t.name}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={s.typeBadge}>{t.campaign_type.replace('_', ' ')}</span>
                  <div style={{ position: 'relative' as const }}>
                    <button
                      style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 16, color: '#aeaeb2', padding: '0 4px', lineHeight: 1, fontWeight: 700 }}
                      onClick={e => { e.stopPropagation(); setMenuOpenId(menuOpenId === t.id ? null : t.id); }}
                    >
                      ···
                    </button>
                    {menuOpenId === t.id && (
                      <div style={{ position: 'absolute' as const, top: '100%', right: 0, zIndex: 100, background: '#fff', border: '1px solid #e8e8ed', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', overflow: 'hidden', minWidth: 150 }}>
                        <button style={s.menuItem} onClick={() => { setEditing(t); setShowModal(true); setMenuOpenId(null); }}>Bearbeiten</button>
                        <button style={s.menuItem} onClick={() => { handleDuplicate(t); setMenuOpenId(null); }}>Duplizieren</button>
                        <button style={{ ...s.menuItem, color: '#ff3b30' }} onClick={() => { handleDelete(t.id); setMenuOpenId(null); }}>Löschen</button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div style={s.tplDesc}>{t.description || '–'}</div>
              {(t.campaign_name_tpl || t.adset_name_tpl) && (
                <div style={s.tplTpl}>
                  {t.campaign_name_tpl && <div style={{ color: '#6e6e73', fontSize: 11 }}>📋 {t.campaign_name_tpl}</div>}
                  {t.adset_name_tpl && <div style={{ color: '#aeaeb2', fontSize: 10 }}>📦 {t.adset_name_tpl}</div>}
                </div>
              )}
              <div style={s.tplMeta}>
                <span style={{ color: '#6e6e73' }}>{t.duration_weeks}W · {t.daily_budget}€/Tag</span>
                <span style={{ color: t.has_form ? '#34c759' : '#aeaeb2', fontSize: 11 }}>
                  {t.has_form ? `✓ ${t.form_template}` : 'Kein Formular'}
                </span>
              </div>
              <div style={s.tplLastUsed}>{formatDate(t.last_used)}</div>
              <button style={s.tplSelectBtn} onClick={() => onSelect(t)}>Auswählen →</button>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <TemplateModal
          template={editing}
          onSave={handleSave}
          onSaveAs={editing ? handleSaveAs : undefined}
          onClose={() => { setShowModal(false); setEditing(undefined); }}
        />
      )}
    </div>
  );
}

// ─── Step 2: Partner selection ─────────────────────────────────────────────────

function Step2Partners({ selectedIds, onToggle, settings, accounts }: {
  selectedIds: string[]; onToggle: (id: string) => void;
  settings: Record<string, any>; accounts: Account[];
}) {
  const [search, setSearch] = useState('');
  const active = accounts.filter(a => a.active);
  const filtered = active.filter(a => {
    const q = search.toLowerCase();
    return !q || resolvedName(a).toLowerCase().includes(q) || (a.location || '').toLowerCase().includes(q);
  });
  const allSelected = filtered.length > 0 && filtered.every(a => selectedIds.includes(a.id));
  const stColor = { ok: '#34c759', warn: '#ff9500', error: '#ff3b30' };

  const toggleAll = () => {
    if (allSelected) filtered.forEach(a => { if (selectedIds.includes(a.id)) onToggle(a.id); });
    else filtered.forEach(a => { if (!selectedIds.includes(a.id)) onToggle(a.id); });
  };

  return (
    <div>
      <div style={s.stepHeader}>
        <div>
          <div style={s.stepTitle}>Partner auswählen</div>
          <div style={s.stepSub}>{selectedIds.length} ausgewählt · {active.length} verfügbar</div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <input style={s.searchInput} value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Partner suchen…" />
          <button style={s.secondaryBtn} onClick={toggleAll}>
            {allSelected ? 'Alle abwählen' : 'Alle auswählen'}
          </button>
        </div>
      </div>

      <div style={s.partnerList}>
        {filtered.map(acc => {
          const name = resolvedName(acc);
          const st = getPartnerStatus(settings[acc.id]);
          const checked = selectedIds.includes(acc.id);
          return (
            <div key={acc.id} style={{ ...s.partnerRow, ...(checked ? s.partnerRowActive : {}) }}
              onClick={() => onToggle(acc.id)}>
              <input type="checkbox" checked={checked} readOnly onClick={e => e.stopPropagation()}
                style={{ marginRight: 12, cursor: 'pointer', flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: '#1d1d1f', display: 'flex', alignItems: 'center', gap: 6 }}>
                  {name}
                  {settings[acc.id] && <span style={s.cfgBadge}>⚙</span>}
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 2 }}>
                  {acc.location && <span style={{ fontSize: 11, color: '#aeaeb2' }}>{acc.location}</span>}
                  <span style={{ fontSize: 10, color: '#c7c7cc' }}>{acc.id}</span>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                {st.missing.length > 0 && (
                  <span style={{ fontSize: 11, color: stColor[st.status], textAlign: 'right' as const }}>
                    {st.missing.slice(0, 2).join(', ')} fehlt{st.missing.length > 2 ? ` +${st.missing.length - 2}` : ''}
                  </span>
                )}
                <span style={{ ...s.statusDot, background: stColor[st.status] }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Partner card (Step 3) ────────────────────────────────────────────────────

function PartnerCard({ id, name, status, cfg, pixels, onUpdate, isOpen, onToggle }: {
  id: string; name: string;
  status: { status: 'ok' | 'warn' | 'error'; missing: string[] };
  cfg: TargetConfig; pixels: Pixel[];
  onUpdate: (p: Partial<TargetConfig>) => void;
  isOpen: boolean; onToggle: () => void;
}) {
  // ── Location search state (per entry, keyed by _id) ──
  const [locQueries, setLocQueries] = useState<Record<string, string>>(() =>
    Object.fromEntries((cfg.locations || []).map(l => [l._id, l.name || '']))
  );
  const [locResults, setLocResults] = useState<Record<string, any[]>>({});
  const [locLoadings, setLocLoadings] = useState<Record<string, boolean>>({});
  const locTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  // ── Page search state ──
  const [accountPages, setAccountPages] = useState<any[]>([]);
  const [pageSearch, setPageSearch] = useState('');
  const [pageOpen, setPageOpen] = useState(false);

  // ── Pixel dropdown ──
  const [pixelOpen, setPixelOpen] = useState(false);

  useEffect(() => {
    getPagesForAccount(id)
      .then(r => setAccountPages(r.data.pages || []))
      .catch(() => {});
  }, [id]);

  const getLocQuery = (locId: string) => locQueries[locId] ?? '';

  const doLocSearch = (locId: string, q: string) => {
    setLocQueries(prev => ({ ...prev, [locId]: q }));
    if (locTimers.current[locId]) clearTimeout(locTimers.current[locId]);
    if (q.length < 2) {
      setLocResults(prev => ({ ...prev, [locId]: [] }));
      setLocLoadings(prev => ({ ...prev, [locId]: false }));
      return;
    }
    setLocLoadings(prev => ({ ...prev, [locId]: true }));
    locTimers.current[locId] = setTimeout(async () => {
      try {
        const r = await searchLocation(q);
        setLocResults(prev => ({ ...prev, [locId]: r.data.results || [] }));
      } catch {}
      setLocLoadings(prev => ({ ...prev, [locId]: false }));
    }, 400);
  };

  const pickLoc = (locId: string, r: any) => {
    setLocQueries(prev => ({ ...prev, [locId]: r.name }));
    setLocResults(prev => ({ ...prev, [locId]: [] }));
    const newLocs = cfg.locations.map(l => l._id === locId ? { ...l, key: r.key, name: r.name, region: r.region } : l);
    onUpdate({ locations: newLocs });
  };

  const updateLocRadius = (locId: string, radius_km: number) => {
    onUpdate({ locations: cfg.locations.map(l => l._id === locId ? { ...l, radius_km } : l) });
  };

  const addLoc = () => {
    const newId = uid();
    setLocQueries(prev => ({ ...prev, [newId]: '' }));
    onUpdate({ locations: [...cfg.locations, { _id: newId, radius_km: 30 }] });
  };

  const removeLoc = (locId: string) => {
    if (cfg.locations.length <= 1) return;
    onUpdate({ locations: cfg.locations.filter(l => l._id !== locId) });
  };

  const stColor = { ok: '#34c759', warn: '#ff9500', error: '#ff3b30' }[status.status];
  const firstLoc = cfg.locations?.[0];
  const hasAnyLoc = cfg.locations?.some(l => l.key);
  const incomplete = !hasAnyLoc || !cfg.pixel_id || !cfg.page_id;

  const sortedPixels = [...pixels].sort((a, b) => {
    const sc = (p: Pixel) => p.is_active === true ? 0 : p.is_active === false ? 2 : 1;
    return sc(a) - sc(b);
  });
  const selectedPixel = sortedPixels.find(p => p.id === cfg.pixel_id);

  const filteredPages = accountPages.filter(p =>
    !pageSearch || p.name.toLowerCase().includes(pageSearch.toLowerCase()) || p.id.includes(pageSearch)
  );
  const selectedPage = accountPages.find(p => p.id === cfg.page_id);

  return (
    <div style={{ ...s.card, borderColor: incomplete ? '#ffccc7' : '#e8e8ed' }}>
      <div style={{ ...s.cardHead, cursor: 'pointer' }} onClick={onToggle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ ...s.statusDot, width: 8, height: 8, background: stColor }} />
          <span style={{ fontWeight: 600, fontSize: 13 }}>{name}</span>
          {firstLoc?.name
            ? <span style={{ fontSize: 11, color: '#aeaeb2' }}>
                · {firstLoc.name}, {firstLoc.radius_km || 30} km
                {cfg.locations.length > 1 && ` +${cfg.locations.length - 1}`}
              </span>
            : <span style={{ fontSize: 11, color: '#ff3b30' }}>· Ort fehlt</span>
          }
        </div>
        <span style={{ color: '#aeaeb2', fontSize: 12 }}>{isOpen ? '▲' : '▼'}</span>
      </div>

      {isOpen && (
        <div style={s.cardBody}>

          {/* ── Orte ── */}
          <div>
            <label style={{ ...s.fieldLabel, display: 'block', marginBottom: 6 }}>
              Orte {!hasAnyLoc && <span style={{ color: '#ff3b30' }}>*</span>}
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {cfg.locations.map((loc, idx) => (
                <div key={loc._id} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                  {/* City search */}
                  <div style={{ flex: 1, position: 'relative' as const }}>
                    <input
                      style={{ ...s.fieldInput, borderColor: !loc.key ? '#ffb3ae' : '#d2d2d7', width: '100%' }}
                      value={getLocQuery(loc._id)}
                      onChange={e => doLocSearch(loc._id, e.target.value)}
                      placeholder="Stadt suchen…"
                    />
                    {locLoadings[loc._id] && (
                      <span style={{ position: 'absolute' as const, right: 10, top: '50%', transform: 'translateY(-50%)', color: '#aeaeb2', fontSize: 11 }}>…</span>
                    )}
                    {(locResults[loc._id] || []).length > 0 && (
                      <div style={s.locDropdown}>
                        {(locResults[loc._id] || []).map((r: any) => (
                          <div key={r.key} style={s.locOption} onClick={() => pickLoc(loc._id, r)}>
                            <span style={{ fontWeight: 500 }}>{r.name}</span>
                            {r.region && <span style={{ color: '#aeaeb2', fontSize: 11 }}> · {r.region}</span>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {/* Radius */}
                  <input
                    type="number"
                    style={{ ...s.fieldInput, width: 72, flexShrink: 0 }}
                    value={loc.radius_km}
                    onChange={e => updateLocRadius(loc._id, Number(e.target.value))}
                    title="Radius km"
                  />
                  <span style={{ fontSize: 11, color: '#aeaeb2', alignSelf: 'center', flexShrink: 0 }}>km</span>
                  {/* Remove button */}
                  <button
                    style={{ background: 'transparent', border: '1px solid #e8e8ed', borderRadius: 7, width: 28, height: 34, cursor: cfg.locations.length > 1 ? 'pointer' : 'default', color: cfg.locations.length > 1 ? '#ff3b30' : '#d2d2d7', fontSize: 16, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    onClick={() => removeLoc(loc._id)}
                    disabled={cfg.locations.length <= 1}
                    title="Ort entfernen"
                  >
                    −
                  </button>
                </div>
              ))}
              <button
                style={{ alignSelf: 'flex-start' as const, background: '#f0f5fb', border: '1px solid #1a3a5c40', borderRadius: 8, padding: '5px 14px', fontSize: 12, color: '#1a3a5c', cursor: 'pointer', fontWeight: 500 }}
                onClick={addLoc}
              >
                + Ort hinzufügen
              </button>
            </div>
          </div>

          <div style={s.row3}>
            <div style={s.fieldGroup2}>
              <label style={s.fieldLabel}>Geschlecht</label>
              <select style={s.fieldInput} value={cfg.gender ?? 0} onChange={e => onUpdate({ gender: Number(e.target.value) })}>
                <option value={0}>Alle</option><option value={1}>Männer</option><option value={2}>Frauen</option>
              </select>
            </div>
            <div style={s.fieldGroup2}>
              <label style={s.fieldLabel}>Budget (€/Tag)</label>
              <input type="number" style={s.fieldInput} value={cfg.budget_euro || 50}
                onChange={e => onUpdate({ budget_euro: Number(e.target.value) })} />
            </div>
          </div>

          <div style={s.row2}>
            {/* ── Pixel custom dropdown ── */}
            <div style={s.fieldGroup2}>
              <label style={s.fieldLabel}>Pixel {!cfg.pixel_id && <span style={{ color: '#ff9500' }}>!</span>}</label>
              <div style={{ position: 'relative' as const }}>
                <div
                  style={{ ...s.fieldInput, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderColor: !cfg.pixel_id ? '#ff9500' : '#d2d2d7', userSelect: 'none' as const }}
                  onClick={() => setPixelOpen(o => !o)}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: 7, overflow: 'hidden' }}>
                    {selectedPixel ? (
                      <>
                        <span style={{ width: 7, height: 7, borderRadius: '50%', flexShrink: 0, background: selectedPixel.is_active === true ? '#34c759' : selectedPixel.is_active === false ? '#aeaeb2' : '#f0f0f5', display: 'inline-block' }} />
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>{selectedPixel.name || selectedPixel.id}</span>
                      </>
                    ) : <span style={{ color: '#aeaeb2' }}>– Pixel wählen –</span>}
                  </span>
                  <span style={{ color: '#aeaeb2', fontSize: 10, flexShrink: 0, marginLeft: 6 }}>{pixelOpen ? '▲' : '▼'}</span>
                </div>
                {pixelOpen && (
                  <>
                    <div style={{ position: 'fixed' as const, inset: 0, zIndex: 98 }} onClick={() => setPixelOpen(false)} />
                    <div style={{ position: 'absolute' as const, top: '100%', left: 0, right: 0, zIndex: 99, background: '#fff', border: '1px solid #d2d2d7', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', overflow: 'hidden', marginTop: 3 }}>
                      {sortedPixels.length === 0
                        ? <div style={{ padding: '10px 12px', color: '#aeaeb2', fontSize: 12 }}>Keine Pixel gefunden</div>
                        : sortedPixels.map(p => (
                          <div key={p.id}
                            style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', cursor: 'pointer', fontSize: 13, background: p.id === cfg.pixel_id ? '#f0f5fb' : 'transparent', borderBottom: '1px solid #f5f5f7' }}
                            onClick={() => { onUpdate({ pixel_id: p.id }); setPixelOpen(false); }}>
                            <span style={{ width: 7, height: 7, borderRadius: '50%', flexShrink: 0, background: p.is_active === true ? '#34c759' : p.is_active === false ? '#aeaeb2' : '#f0f0f5' }} />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontWeight: p.id === cfg.pixel_id ? 600 : 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>{p.name || p.id}</div>
                              <div style={{ fontSize: 10, color: '#aeaeb2' }}>{p.id}</div>
                            </div>
                            {p.is_active === true && <span style={{ fontSize: 10, color: '#34c759', flexShrink: 0 }}>aktiv</span>}
                          </div>
                        ))
                      }
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* ── Facebook page search ── */}
            <div style={s.fieldGroup2}>
              <label style={s.fieldLabel}>Facebook-Seite {!cfg.page_id && <span style={{ color: '#ff9500' }}>!</span>}</label>
              <div style={{ position: 'relative' as const }}>
                {selectedPage && !pageOpen ? (
                  <div
                    style={{ ...s.fieldInput, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderColor: '#d2d2d7', userSelect: 'none' as const }}
                    onClick={() => { setPageSearch(''); setPageOpen(true); }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>{selectedPage.name}</div>
                      <div style={{ fontSize: 10, color: '#aeaeb2' }}>{selectedPage.id}</div>
                    </div>
                    <span style={{ color: '#aeaeb2', fontSize: 10, flexShrink: 0, marginLeft: 6 }}>▼</span>
                  </div>
                ) : (
                  <input
                    style={{ ...s.fieldInput, borderColor: !cfg.page_id ? '#ff9500' : '#d2d2d7', width: '100%' }}
                    value={pageSearch}
                    onChange={e => { setPageSearch(e.target.value); setPageOpen(true); }}
                    onFocus={() => setPageOpen(true)}
                    placeholder="Seite suchen…"
                    autoFocus={pageOpen && !selectedPage}
                  />
                )}
                {pageOpen && (
                  <>
                    <div style={{ position: 'fixed' as const, inset: 0, zIndex: 98 }} onClick={() => setPageOpen(false)} />
                    <div style={{ position: 'absolute' as const, top: '100%', left: 0, right: 0, zIndex: 99, background: '#fff', border: '1px solid #d2d2d7', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', overflow: 'hidden', marginTop: 3, maxHeight: 220, overflowY: 'auto' as const }}>
                      {filteredPages.length === 0
                        ? <div style={{ padding: '10px 12px', color: '#aeaeb2', fontSize: 12 }}>{accountPages.length === 0 ? 'Lade Seiten…' : 'Keine Ergebnisse'}</div>
                        : filteredPages.map(p => (
                          <div key={p.id}
                            style={{ padding: '9px 12px', cursor: 'pointer', fontSize: 13, background: p.id === cfg.page_id ? '#f0f5fb' : 'transparent', borderBottom: '1px solid #f5f5f7' }}
                            onClick={() => { onUpdate({ page_id: p.id }); setPageOpen(false); setPageSearch(''); }}>
                            <div style={{ fontWeight: p.id === cfg.page_id ? 600 : 400 }}>{p.name}</div>
                            <div style={{ fontSize: 10, color: '#aeaeb2' }}>{p.id}</div>
                          </div>
                        ))
                      }
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* ── WordPress-Weiterleitung (über dem Webseitenlink) ── */}
          <div style={{ background: '#f0f5fb', border: '1px solid #c7d2dd', borderRadius: 10, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#1a3a5c' }}>
              <input type="checkbox" checked={!!cfg.use_wordpress}
                onChange={e => onUpdate({ use_wordpress: e.target.checked })} />
              Zur WordPress-Seite weiterleiten
            </label>
            <div style={s.fieldGroup2}>
              <label style={s.fieldLabel}>
                WordPress-URL {cfg.use_wordpress && !cfg.wordpress_url && <span style={{ color: '#ff9500' }}>– bitte eintragen</span>}
              </label>
              <input style={{ ...s.fieldInput, ...(cfg.use_wordpress && !cfg.wordpress_url ? { borderColor: '#ff9500' } : {}) }}
                value={cfg.wordpress_url || ''}
                onChange={e => onUpdate({ wordpress_url: e.target.value })}
                placeholder="https://…  (falls nicht bekannt, hier eintragen & speichern)" />
              <span style={{ fontSize: 11, color: '#6e6e73' }}>
                Wird gemerkt und ist im Partner-Profil änderbar. Bei aktivierter Weiterleitung
                wird dieser Link als Ad-Ziel verwendet.
              </span>
            </div>
          </div>

          <div style={s.row2}>
            <div style={s.fieldGroup2}>
              <label style={s.fieldLabel}>Impressum-URL</label>
              <input style={s.fieldInput} value={cfg.impressum_url || ''}
                onChange={e => onUpdate({ impressum_url: e.target.value })} placeholder="https://…/impressum" />
            </div>
            <div style={s.fieldGroup2}>
              <label style={s.fieldLabel}>Webseite {cfg.use_wordpress && <span style={{ color: '#aeaeb2', fontWeight: 400 }}>(inaktiv – WordPress aktiv)</span>}</label>
              <input style={{ ...s.fieldInput, ...(cfg.use_wordpress ? { opacity: 0.5 } : {}) }} value={cfg.website_url || ''}
                onChange={e => onUpdate({ website_url: e.target.value })} placeholder="https://…" />
            </div>
          </div>

          <div style={s.fieldGroup2}>
            <label style={s.fieldLabel}>Angezeigter Link</label>
            <input style={s.fieldInput} value={cfg.display_url || ''}
              onChange={e => onUpdate({ display_url: e.target.value })} placeholder="z.B. deinstudio.de" />
            <span style={{ fontSize: 11, color: '#6e6e73' }}>Wird in der Anzeige als Link-Text angezeigt und gemerkt.</span>
          </div>

          <div style={s.fieldGroup2}>
            <label style={s.fieldLabel}>Startdatum</label>
            <input type="date" style={{ ...s.fieldInput, maxWidth: 180 }} value={cfg.start_date || ''}
              onChange={e => onUpdate({ start_date: e.target.value })} />
          </div>

          <div style={{ padding: '8px 12px', background: '#f5f5f7', borderRadius: 8, fontSize: 12, color: '#aeaeb2' }}>
            🎬 Creatives (Higgsfield) · coming soon
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Step 3: Settings ─────────────────────────────────────────────────────────

function Step3Settings({ selectedIds, accounts, settings, configs, onUpdate, pixels }: {
  selectedIds: string[]; accounts: Account[]; settings: Record<string, any>;
  configs: Record<string, TargetConfig>; onUpdate: (id: string, p: Partial<TargetConfig>) => void;
  pixels: Record<string, Pixel[]>;
}) {
  const [expanded, setExpanded] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(selectedIds.map(id => [id, true]))
  );
  const [globalMode, setGlobalMode] = useState(false);
  const [global, setGlobal] = useState({ gender: 0, budget_euro: 50 });

  return (
    <div>
      <div style={s.stepHeader}>
        <div>
          <div style={s.stepTitle}>Einstellungen prüfen</div>
          <div style={s.stepSub}>{selectedIds.length} Partner konfigurieren</div>
        </div>
        <button style={{ ...s.secondaryBtn, ...(globalMode ? { background: '#1a3a5c', color: '#fff', borderColor: '#1a3a5c' } : {}) }}
          onClick={() => setGlobalMode(g => !g)}>
          Gleiche Einstellungen für alle
        </button>
      </div>

      {globalMode && (
        <div style={s.globalRow}>
          <div style={s.fieldGroup2}>
            <label style={s.fieldLabel}>Budget (€/Tag)</label>
            <input type="number" style={{ ...s.fieldInput, width: 90 }} value={global.budget_euro}
              onChange={e => setGlobal(g => ({ ...g, budget_euro: Number(e.target.value) }))} />
          </div>
          <div style={s.fieldGroup2}>
            <label style={s.fieldLabel}>Geschlecht</label>
            <select style={{ ...s.fieldInput, width: 130 }} value={global.gender}
              onChange={e => setGlobal(g => ({ ...g, gender: Number(e.target.value) }))}>
              <option value={0}>Alle</option><option value={1}>Männer</option><option value={2}>Frauen</option>
            </select>
          </div>
          <button style={{ ...s.primaryBtn, alignSelf: 'flex-end' as const }}
            onClick={() => selectedIds.forEach(id => onUpdate(id, global))}>
            Für alle übernehmen
          </button>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {selectedIds.map(id => {
          const acc = accounts.find(a => a.id === id);
          if (!acc) return null;
          return (
            <PartnerCard key={id} id={id} name={resolvedName(acc)}
              status={getPartnerStatus(settings[id])}
              cfg={configs[id]}
              pixels={pixels[id] || []}
              onUpdate={p => onUpdate(id, p)}
              isOpen={expanded[id] ?? true}
              onToggle={() => setExpanded(e => ({ ...e, [id]: !e[id] }))} />
          );
        })}
      </div>
    </div>
  );
}

// ─── Step 4: Summary ──────────────────────────────────────────────────────────

function Step4Summary({ selectedIds, accounts, configs, template, mode, sourceCampaignId, onSourceCampaignIdChange, onStart, pushStarted, onCancelPush }: {
  selectedIds: string[]; accounts: Account[]; configs: Record<string, TargetConfig>;
  template: Template | null; mode: 'new' | 'copy'; sourceCampaignId: string; onSourceCampaignIdChange: (v: string) => void;
  onStart: () => void; pushStarted: boolean; onCancelPush: () => void;
}) {
  const [srcAccId, setSrcAccId] = useState(() => accounts[0]?.id || '');
  const [campaigns, setCampaigns] = useState<{ id: string; name: string; status: string }[]>([]);
  const [loadingCamps, setLoadingCamps] = useState(false);

  useEffect(() => {
    if (!pushStarted) return;
    const t = setTimeout(onCancelPush, 5000);
    return () => clearTimeout(t);
  }, [pushStarted]);

  useEffect(() => {
    if (!srcAccId) return;
    setLoadingCamps(true);
    getPushCampaigns(srcAccId)
      .then(r => setCampaigns(r.data.campaigns || []))
      .catch(() => setCampaigns([]))
      .finally(() => setLoadingCamps(false));
  }, [srcAccId]);

  const rows = selectedIds.map(id => {
    const acc = accounts.find(a => a.id === id);
    const cfg = configs[id];
    const firstLoc = cfg.locations?.[0];
    const ready = !!(cfg.locations?.some(l => l.key) && cfg.pixel_id && cfg.page_id);
    return { id, name: acc ? resolvedName(acc) : id, cfg, firstLoc, ready };
  });
  const readyCount = rows.filter(r => r.ready).length;

  const campName = (cfg: TargetConfig) => {
    const loc = cfg.locations?.[0];
    return (template?.campaign_name_tpl || '–')
      .replace('{datum}', todayISO())
      .replace('{ort}', loc?.name || '?')
      .replace('{radius}', String(loc?.radius_km || 30))
      .replace('{km}', String(loc?.radius_km || 30));
  };

  return (
    <div>
      <div style={s.stepHeader}>
        <div>
          <div style={s.stepTitle}>Zusammenfassung</div>
          <div style={s.stepSub}>{readyCount} von {rows.length} Partner bereit für Push</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button style={{ ...s.primaryBtn, opacity: (readyCount === 0 || pushStarted || (mode === 'copy' && !sourceCampaignId.trim())) ? 0.4 : 1 }}
            disabled={readyCount === 0 || pushStarted || (mode === 'copy' && !sourceCampaignId.trim())} onClick={onStart}>
            {pushStarted ? 'Push läuft…' : `Push starten für ${readyCount} Partner →`}
          </button>
          {pushStarted && (
            <button onClick={onCancelPush}
              style={{ background: 'none', border: 'none', color: '#aeaeb2', fontSize: 13, cursor: 'pointer', padding: '0 4px', textDecoration: 'underline' }}>
              Abbrechen
            </button>
          )}
        </div>
      </div>

      {mode === 'copy' && (
        <div style={{ marginBottom: 20, padding: '14px 16px', background: sourceCampaignId ? '#f0f9f0' : '#fff8f0', border: `1px solid ${sourceCampaignId ? '#34c759' : '#ff9500'}`, borderRadius: 10 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#1d1d1f', marginBottom: 8 }}>
            Quell-Kampagne für Ads
            {!sourceCampaignId && <span style={{ color: '#ff9500', marginLeft: 6 }}>⚠ Pflichtfeld – ohne Kampagne werden keine Ads erstellt</span>}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <select
              style={{ flex: '0 0 auto', padding: '7px 10px', border: '1px solid #d1d1d6', borderRadius: 8, fontSize: 12, background: '#fff', minWidth: 170, maxWidth: 220 }}
              value={srcAccId}
              onChange={e => { setSrcAccId(e.target.value); onSourceCampaignIdChange(''); }}
            >
              {accounts.map(a => (
                <option key={a.id} value={a.id}>{resolvedName(a) || a.id}</option>
              ))}
            </select>
            <select
              style={{ flex: 1, padding: '7px 10px', border: '1px solid #d1d1d6', borderRadius: 8, fontSize: 12, background: '#fff' }}
              value={sourceCampaignId}
              onChange={e => onSourceCampaignIdChange(e.target.value)}
              disabled={loadingCamps}
            >
              <option value="">{loadingCamps ? 'Lade Kampagnen…' : campaigns.length === 0 ? 'Keine Kampagnen gefunden' : '– Kampagne auswählen –'}</option>
              {campaigns.map(c => (
                <option key={c.id} value={c.id}>{c.name}{c.status !== 'ACTIVE' ? ` [${c.status}]` : ''}</option>
              ))}
            </select>
          </div>
          {sourceCampaignId && (
            <div style={{ fontSize: 11, color: '#6e6e73', marginTop: 5, fontFamily: 'monospace' }}>ID: {sourceCampaignId}</div>
          )}
        </div>
      )}

      {template && (
        <div style={{ marginBottom: 20, padding: '14px 16px', background: '#f5f7fa', border: '1px solid #e8e8ed', borderRadius: 10 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#1d1d1f', marginBottom: 8 }}>
            Das wird für jeden Partner angelegt
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap' as const, gap: '6px 18px', fontSize: 12, color: '#6e6e73' }}>
            <span>Alter <b>{template.age_min ?? 35}–{template.age_max ?? 65}</b></span>
            <span>Platzierungen <b>{template.facebook_only ? 'nur Facebook' : 'Advantage+'}</b></span>
            <span>Conversion-Ort <b>{template.destination === 'ON_AD' ? 'Instantformular' : 'Website'}</b></span>
            {template.ad_sets && template.ad_sets.length > 0 ? (
              <span>Struktur <b>{template.ad_sets.length} Anzeigengruppen</b> /{' '}
                <b>{template.ad_sets.reduce((n, g) => n + (g.ads?.length || 0), 0)} Anzeigen</b>{' '}
                {template.use_placeholder_image ? '(graue Platzhalter)' : '(Bild aus Link-Vorschau)'}</span>
            ) : (
              <span>Struktur <b>1 Anzeigengruppe</b> / <b>{template.ads_count ?? 6} Anzeigen</b>{' '}
                {template.use_placeholder_image ? '(graue Platzhalter)' : '(Bild aus Link-Vorschau)'}</span>
            )}
            <span>Formular <b>{template.has_form ? template.form_template : 'keins'}</b></span>
            <span>Budget <b>{template.daily_budget}€/Tag (CBO)</b></span>
            <span>Status <b>PAUSED</b></span>
          </div>
          <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #e8e8ed', fontSize: 12, color: '#c05000' }}>
            Danach noch manuell: Motive statt der Platzhalter hochladen · Pixel auf der Landingpage prüfen ·
            Copy gegenlesen · erst dann aktivieren.
          </div>
        </div>
      )}

      <div style={{ overflowX: 'auto' as const }}>
        <table style={s.table}>
          <thead>
            <tr>{['Partner', 'Kampagnenname', 'Orte', 'Budget', 'Start', 'Pixel', 'Formular', 'Status'].map(h => (
              <th key={h} style={s.th}>{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} style={{ background: r.ready ? '#fff' : '#fff8f8' }}>
                <td style={s.td}>{r.name}</td>
                <td style={{ ...s.td, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const, fontSize: 11 }}>{campName(r.cfg)}</td>
                <td style={s.td}>
                  {r.cfg.locations?.some(l => l.key)
                    ? <span>{r.cfg.locations.filter(l => l.key).map(l => `${l.name} (${l.radius_km}km)`).join(', ')}</span>
                    : <span style={{ color: '#ff3b30' }}>fehlt</span>}
                </td>
                <td style={s.td}>{r.cfg.budget_euro || 50} €</td>
                <td style={s.td}>{r.cfg.start_date
                  ? new Date(r.cfg.start_date).toLocaleDateString('de-DE')
                  : <span style={{ color: '#aeaeb2' }}>sofort</span>}</td>
                <td style={s.td}>{r.cfg.pixel_id ? <span style={{ color: '#34c759' }}>✓</span> : <span style={{ color: '#ff9500' }}>fehlt</span>}</td>
                <td style={s.td}>{template?.has_form ? <span style={{ color: '#34c759' }}>✓ {template.form_template}</span> : '–'}</td>
                <td style={s.td}>
                  <span style={{ color: r.ready ? '#34c759' : '#ff3b30', fontWeight: 600, fontSize: 12 }}>
                    {r.ready ? '✓ Bereit' : '✕ Unvollständig'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Ad Text Modal ────────────────────────────────────────────────────────────

interface AdEntry {
  ad_id: string; ad_name: string; creative_id: string; campaign_name: string;
  adset_name?: string;
  headline: string; message: string; description: string; cta: string;
}

function AdTextModal({ successIds, accounts, onClose }: {
  successIds: string[]; accounts: Account[]; onClose: () => void;
}) {
  const successAccounts = accounts.filter(a => successIds.includes(a.id));
  const [selAccId, setSelAccId] = useState(successAccounts[0]?.id || '');
  const [ads, setAds] = useState<AdEntry[]>([]);
  const [loadingAds, setLoadingAds] = useState(false);
  const [edited, setEdited] = useState<Record<string, Partial<AdEntry>>>({});
  const [saving, setSaving] = useState<Record<string, boolean>>({});
  const [saved, setSaved] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!selAccId) return;
    setLoadingAds(true);
    setAds([]);
    getAdTexts(selAccId)
      .then(r => setAds(r.data.ads || []))
      .catch(() => setAds([]))
      .finally(() => setLoadingAds(false));
  }, [selAccId]);

  const editAd = (adId: string, field: string, value: string) =>
    setEdited(e => ({ ...e, [adId]: { ...(e[adId] || {}), [field]: value } }));

  const saveAd = async (ad: AdEntry) => {
    setSaving(sv => ({ ...sv, [ad.ad_id]: true }));
    try {
      await updateAdText({
        account_id: selAccId, ad_id: ad.ad_id, creative_id: ad.creative_id,
        ...(edited[ad.ad_id] || {}),
      });
      setSaved(sv => ({ ...sv, [ad.ad_id]: true }));
    } finally {
      setSaving(sv => ({ ...sv, [ad.ad_id]: false }));
    }
  };

  return (
    <div style={ms.overlay}>
      <div style={{ ...ms.modal, maxWidth: 800 }}>
        <div style={ms.header}>
          <span style={ms.title}>Kampagnentexte bearbeiten</span>
          <button style={ms.closeBtn} onClick={onClose}>×</button>
        </div>
        {successAccounts.length > 1 && (
          <div style={{ padding: '10px 24px', borderBottom: '1px solid #e8e8ed', display: 'flex', gap: 8, flexWrap: 'wrap' as const }}>
            {successAccounts.map(a => (
              <button key={a.id} onClick={() => setSelAccId(a.id)} style={{
                borderRadius: 8, padding: '5px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                background: selAccId === a.id ? '#1a3a5c' : '#f5f5f7',
                color: selAccId === a.id ? '#fff' : '#1d1d1f',
                border: selAccId === a.id ? 'none' : '1px solid #e8e8ed',
              }}>
                {a.name}
              </button>
            ))}
          </div>
        )}
        <div style={{ ...ms.body, gap: 0 }}>
          {loadingAds && <div style={{ color: '#6e6e73', fontSize: 13, padding: '8px 0' }}>Lade Anzeigentexte…</div>}
          {!loadingAds && ads.length === 0 && (
            <div style={{ color: '#aeaeb2', fontSize: 13 }}>Keine Anzeigen gefunden.</div>
          )}
          {ads.map(ad => {
            const e = edited[ad.ad_id] || {};
            return (
              <div key={ad.ad_id} style={{ border: '1px solid #e8e8ed', borderRadius: 12, padding: '14px 16px', marginBottom: 12 }}>
                <div style={{ fontSize: 11, color: '#aeaeb2', marginBottom: 10 }}>
                  {ad.campaign_name}{ad.adset_name ? ` · ${ad.adset_name}` : ''} · {ad.ad_name}
                </div>
                <div style={{ marginBottom: 8 }}>
                  <label style={ms.label}>Headline</label>
                  <input type="text" value={e.headline ?? ad.headline}
                    onChange={ev => editAd(ad.ad_id, 'headline', ev.target.value)} style={{ ...ms.input, width: '100%', boxSizing: 'border-box' as const }} />
                </div>
                <div style={{ marginBottom: 8 }}>
                  <label style={ms.label}>Anzeigentext</label>
                  <textarea value={e.message ?? ad.message}
                    onChange={ev => editAd(ad.ad_id, 'message', ev.target.value)}
                    style={{ ...ms.input, width: '100%', boxSizing: 'border-box' as const, minHeight: 72, resize: 'vertical' as const }} />
                </div>
                <div style={{ marginBottom: 10 }}>
                  <label style={ms.label}>Beschreibung</label>
                  <input type="text" value={e.description ?? ad.description}
                    onChange={ev => editAd(ad.ad_id, 'description', ev.target.value)} style={{ ...ms.input, width: '100%', boxSizing: 'border-box' as const }} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8 }}>
                  {saved[ad.ad_id] && <span style={{ fontSize: 12, color: '#34c759' }}>✓ Gespeichert</span>}
                  <button style={{ ...ms.saveBtn, padding: '7px 16px', fontSize: 12 }}
                    disabled={saving[ad.ad_id]} onClick={() => saveAd(ad)}>
                    {saving[ad.ad_id] ? 'Speichert…' : 'Speichern'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── Step 5: Push ─────────────────────────────────────────────────────────────

type RetryState = { logs: { type: string; msg: string }[]; running: boolean; done: boolean; success: boolean };

function Step5Push({ selectedIds, accounts, configs, template, custSettings, sourceCampaignId }: {
  selectedIds: string[]; accounts: Account[];
  configs: Record<string, TargetConfig>; template: Template | null;
  custSettings: Record<string, any>; sourceCampaignId: string;
}) {
  const [logs, setLogs] = useState<Array<{ type: string; msg: string }>>([]);
  const [done, setDone] = useState(0);
  const [finished, setFinished] = useState(false);
  const [startError, setStartError] = useState('');
  const [summary, setSummary] = useState<{ success: number; failed: string[] } | null>(null);
  const [showAdTextModal, setShowAdTextModal] = useState(false);
  const [retryStates, setRetryStates] = useState<Record<string, RetryState>>({});
  const logEndRef = useRef<HTMLDivElement>(null);
  const esRef = useRef<EventSource | null>(null);
  const didStart = useRef(false);
  const total = selectedIds.length;

  const today = new Date().toISOString().slice(0, 10);
  const replaceVars = (str: string, cfg: TargetConfig) => {
    const loc = cfg.locations?.[0];
    return str
      .replace(/{datum}/g, today)
      .replace(/{ort}/g, loc?.name || '')
      .replace(/{radius}/g, String(loc?.radius_km || 30))
      .replace(/{km}/g, String(loc?.radius_km || 30));
  };
  const buildTarget = (id: string) => {
    const cfg = configs[id];
    const cst = custSettings[id] || {};
    const firstLoc = cfg.locations?.[0];
    return {
      account_id: id,
      campaign_name: replaceVars(template?.campaign_name_tpl || 'Neue Kampagne', cfg),
      adset_name: replaceVars(template?.adset_name_tpl || 'AdSet', cfg),
      location_key: firstLoc?.key || '',
      location_name: firstLoc?.name || '',
      radius_km: firstLoc?.radius_km || 30,
      locations: (cfg.locations || []).filter(l => l.key).map(l => ({
        key: l.key!, name: l.name || '', radius_km: l.radius_km,
      })),
      gender: cfg.gender || 0,
      pixel_id: cfg.pixel_id || '',
      page_id: cfg.page_id || '',
      budget_euro: cfg.budget_euro || 50,
      impressum_url: cfg.impressum_url || '',
      create_form: template?.has_form || false,
      form_template: template?.form_template || 'soka',
      campaign_type: template?.campaign_type || 'LEAD_GENERATION',
      // Kampagnen-Setup aus dem Template
      age_min: template?.age_min ?? 35,
      age_max: template?.age_max ?? 65,
      ads_count: template?.ads_count ?? 6,
      duration_weeks: template?.duration_weeks ?? 6,
      facebook_only: !!template?.facebook_only,
      // Nur wenn im Template explizit gesetzt – aeltere Templates ziehen weiter
      // das Vorschaubild der verlinkten Seite.
      use_placeholder_image: !!template?.use_placeholder_image,
      destination: template?.destination || 'WEBSITE',
      start_date: cfg.start_date || '',
      // Anzeigengruppen inkl. eigener Texte; Variablen werden hier aufgeloest
      ad_sets: (template?.ad_sets || []).map(g => ({
        name: replaceVars(g.name || '', cfg),
        age_min: g.age_min ?? template?.age_min ?? 35,
        age_max: g.age_max ?? template?.age_max ?? 65,
        gender: g.gender ?? cfg.gender ?? 0,
        radius_km: g.radius_km ?? null,
        ads: (g.ads || []).map(a => ({
          name: replaceVars(a.name || '', cfg),
          headline: replaceVars(a.headline || '', cfg),
          copy_text: replaceVars(a.copy_text || '', cfg),
          description: replaceVars(a.description || '', cfg),
          cta: a.cta || template?.cta_type || 'SIGN_UP',
        })),
      })),
      // Ad-Copy + Links aus Template und Partnereinstellungen
      source_campaign_id: sourceCampaignId || template?.source_campaign_id || '',
      copy_text: template?.copy_text || '',
      headline: template?.headline || '',
      // Ziel-Link: WordPress (falls aktiviert) > vom Nutzer eingegebener Webseiten-Link >
      // gespeicherte Partner-URLs. Der eingegebene Link gewinnt IMMER über die alte SOKA-URL.
      landingpage_url: (cfg.use_wordpress && cfg.wordpress_url)
        ? cfg.wordpress_url
        : (cfg.website_url || cst.landingpage_url || cst.soka_ad_url || ''),
      website_url: cfg.website_url || '',
      wordpress_url: cfg.wordpress_url || '',
      use_wordpress: !!cfg.use_wordpress,
      display_url: cfg.display_url || cst.display_url || '',
      cta: cst.cta || template?.cta_type || 'SIGN_UP',
    };
  };

  const handleRetry = (accId: string) => {
    setRetryStates(rs => ({ ...rs, [accId]: { logs: [], running: true, done: false, success: false } }));
    const retryTarget = buildTarget(accId);
    console.log('Retry payload:', retryTarget);
    startNewCampaign({ targets: [retryTarget] })
      .then(res => {
        const jobId: string = res.data.job_id;
        const es = new EventSource(`${SSE_BASE}/${jobId}`);
        es.onmessage = (event) => {
          const data = JSON.parse(event.data);
          if (data.type === 'stream_end') { es.close(); return; }
          const isDone = data.type === 'done';
          setRetryStates(rs => ({
            ...rs,
            [accId]: {
              ...rs[accId],
              logs: [...(rs[accId]?.logs || []), { type: data.type, msg: data.msg }],
              running: !isDone,
              done: isDone || rs[accId]?.done,
              success: isDone,
            },
          }));
          if (isDone) es.close();
        };
        es.onerror = () => {
          setRetryStates(rs => ({ ...rs, [accId]: { ...rs[accId], running: false, done: true } }));
          es.close();
        };
      })
      .catch(() => {
        setRetryStates(rs => ({ ...rs, [accId]: { ...rs[accId], running: false, done: true } }));
      });
  };

  const runPush = () => {
    esRef.current?.close();
    setLogs([{ type: 'info', msg: `Verbinde mit Backend… (${total} Partner)` }]);
    setDone(0);
    setFinished(false);
    setSummary(null);
    setStartError('');
    setRetryStates({});

    const targets = selectedIds.map(buildTarget);
    console.log('Push payload:', targets);

    startNewCampaign({ targets })
      .then(res => {
        const jobId: string = res.data.job_id;
        setLogs(l => [...l, { type: 'info', msg: `Job gestartet: ${jobId}` }]);

        const es = new EventSource(`${SSE_BASE}/${jobId}`);
        esRef.current = es;
        const failedPartners: string[] = [];

        es.onmessage = (event) => {
          const data = JSON.parse(event.data);
          if (data.type === 'stream_end') { es.close(); return; }
          setLogs(l => [...l, { type: data.type, msg: data.msg }]);
          if (data.type === 'target_done') setDone(d => d + 1);
          if (data.type === 'error' && data.msg?.startsWith('Fehlgeschlagen:')) {
            failedPartners.push(...data.msg.replace('Fehlgeschlagen:', '').split(',').map((s: string) => s.trim()));
          }
          if (data.type === 'done') {
            setFinished(true);
            setSummary({ success: total - failedPartners.length, failed: [...failedPartners] });
            es.close();
          }
          setTimeout(() => logEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
        };

        es.onerror = () => {
          setLogs(l => [...l, { type: 'error', msg: '⚠ Verbindung zum Server unterbrochen' }]);
          es.close();
        };
      })
      .catch(err => {
        setStartError(`Push konnte nicht gestartet werden: ${err.message || 'Unbekannter Fehler'}`);
      });
  };

  useEffect(() => {
    if (didStart.current) return;
    didStart.current = true;
    runPush();
    return () => { esRef.current?.close(); };
  }, []);

  const colors: Record<string, string> = {
    info: '#6e6e73', success: '#34c759', warning: '#ff9500',
    error: '#ff3b30', target_done: '#60a5fa', done: '#34c759',
  };

  return (
    <div>
      <div style={s.stepHeader}>
        <div>
          <div style={s.stepTitle}>{finished ? '✓ Push abgeschlossen' : 'Push läuft…'}</div>
          <div style={s.stepSub}>{done} / {total} Partner abgeschlossen</div>
        </div>
        {finished && summary && (
          <span style={{ color: summary.failed.length ? '#ff3b30' : '#34c759', fontWeight: 600, fontSize: 14 }}>
            {summary.success}/{total} erfolgreich
          </span>
        )}
      </div>

      {startError && (
        <div style={{ background: '#fff0f0', border: '1px solid #ffd0d0', borderRadius: 8, padding: '10px 14px', marginBottom: 12, color: '#ff3b30', fontSize: 14 }}>
          {startError}
        </div>
      )}

      <div style={s.progressWrap}>
        <div style={{ ...s.progressFill, width: `${(done / total) * 100}%` }} />
      </div>

      <div style={s.logBox}>
        {logs.map((l, i) => (
          <div key={i} style={{ ...s.logLine, color: colors[l.type] || '#6e6e73', fontWeight: l.type === 'done' || l.type === 'target_done' ? 600 : 400 }}>
            {l.msg}
          </div>
        ))}
        {!finished && !startError && <div style={{ ...s.logLine, color: '#555' }}>⏳ Läuft…</div>}
        <div ref={logEndRef} />
      </div>

      {(finished || startError) && (
        <button
          onClick={runPush}
          style={{
            marginTop: 14, width: '100%', background: '#1a3a5c',
            border: 'none', borderRadius: 10, padding: '11px 0',
            color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer',
          }}>
          ↻ Nochmal machen
        </button>
      )}

      {finished && summary && summary.failed.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontWeight: 600, fontSize: 13, color: '#c05000', marginBottom: 8 }}>Fehlgeschlagene Partner:</div>
          {summary.failed.map((failedId, i) => {
            const acc = accounts.find(a => a.id === failedId);
            const partnerName = acc ? resolvedName(acc) : failedId;
            const retry = retryStates[failedId];
            return (
              <div key={i} style={{ marginBottom: 8, background: '#fff8f0', border: '1px solid #ffd0a0', borderRadius: 8, padding: '10px 14px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 13, color: '#c05000' }}>• {partnerName}</span>
                  {retry?.running
                    ? <span style={{ fontSize: 12, color: '#6e6e73' }}>läuft…</span>
                    : (
                      <button onClick={() => handleRetry(failedId)} style={{
                        fontSize: 12, fontWeight: 600, color: '#1a3a5c', background: '#fff',
                        border: '1px solid #1a3a5c', borderRadius: 7, padding: '4px 10px', cursor: 'pointer',
                      }}>
                        {retry?.done && retry.success ? '↻ Nochmals' : 'Erneut versuchen'}
                      </button>
                    )
                  }
                </div>
                {retry && retry.logs.length > 0 && (
                  <div style={{ marginTop: 8, fontFamily: 'monospace', fontSize: 11, maxHeight: 120, overflowY: 'auto' as const, color: '#6e6e73' }}>
                    {retry.logs.map((l, li) => (
                      <div key={li} style={{ color: colors[l.type] || '#6e6e73' }}>{l.msg}</div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {finished && summary && summary.success > 0 && (() => {
        const successIds = selectedIds.filter(id => !summary.failed.includes(id));
        return (
          <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#1d1d1f', marginBottom: 10 }}>
              Werbeanzeigenmanager öffnen:
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {successIds.map(id => {
                const acc = accounts.find(a => a.id === id);
                const numId = id.replace('act_', '');
                return (
                  <a key={id}
                    href={`https://adsmanager.facebook.com/adsmanager/manage/campaigns?act=${numId}`}
                    target="_blank" rel="noreferrer"
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '8px 12px', background: '#f5f5f7', borderRadius: 8,
                      textDecoration: 'none', color: '#1a3a5c', fontSize: 13, fontWeight: 500,
                      border: '1px solid #e8e8ed',
                    }}>
                    <span>{acc?.name || id}</span>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                      <polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
                    </svg>
                  </a>
                );
              })}
            </div>
            <button
              onClick={() => setShowAdTextModal(true)}
              style={{
                marginTop: 14, width: '100%', background: '#fff',
                border: '1.5px solid #1a3a5c', borderRadius: 10, padding: '10px 0',
                color: '#1a3a5c', fontSize: 13, fontWeight: 600, cursor: 'pointer',
              }}>
              Kampagnentexte bearbeiten
            </button>
            {showAdTextModal && (
              <AdTextModal
                successIds={successIds}
                accounts={accounts}
                onClose={() => setShowAdTextModal(false)}
              />
            )}
          </div>
        );
      })()}
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function CampaignPush({ user, onLogout, onNavigate }: Props) {
  const [mode, setMode] = useState<'new' | 'copy'>('new');
  const [step, setStep] = useState(1);
  const [template, setTemplate] = useState<Template | null>(null);
  const [sourceCampaignId, setSourceCampaignId] = useState('');
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [custSettings, setCustSettings] = useState<Record<string, any>>({});
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [configs, setConfigs] = useState<Record<string, TargetConfig>>({});
  const [pixels, setPixels] = useState<Record<string, Pixel[]>>({});
  const [loading, setLoading] = useState(true);
  const [pushStarted, setPushStarted] = useState(false);

  useEffect(() => {
    Promise.all([getAdAccounts(), getCustomerSettings()])
      .then(([aR, sR]) => {
        setAccounts(aR.data.accounts || []);
        setCustSettings(sR.data.by_account || {});
        setLoading(false);
      }).catch(() => setLoading(false));
  }, []);

  const togglePartner = (id: string) =>
    setSelectedIds(ids => ids.includes(id) ? ids.filter(i => i !== id) : [...ids, id]);

  const makeDefaultConfig = (id: string): TargetConfig => {
    const st = custSettings[id] || {};
    const savedLocs = st.locations as LocationEntry[] | undefined;
    const initLoc: LocationEntry = savedLocs?.[0] ?? {
      _id: uid(),
      key: st.location_key || undefined,
      name: st.location || undefined,
      radius_km: st.radius_km || 30,
    };
    const locations = savedLocs && savedLocs.length > 0
      ? savedLocs.map((l, i) => ({ ...l, _id: l._id || uid() }))
      : [initLoc];
    return {
      locations,
      gender: st.gender ?? 0,
      pixel_id: st.pixel_id || '',
      page_id: st.page_id || '',
      budget_euro: st.budget_euro || template?.daily_budget || 50,
      impressum_url: st.impressum_url || '',
      website_url: st.soka_ad_url || '',
      display_url: st.display_url || '',
      wordpress_url: st.wordpress_url || '',
      use_wordpress: false,
      start_date: '',
    };
  };

  const updateConfig = (id: string, patch: Partial<TargetConfig>) =>
    setConfigs(c => ({ ...c, [id]: { ...(c[id] || makeDefaultConfig(id)), ...patch } }));

  const goStep = async (next: number) => {
    if (next === 3) {
      const newCfgs = { ...configs };
      selectedIds.forEach(id => { if (!newCfgs[id]) newCfgs[id] = makeDefaultConfig(id); });
      setConfigs(newCfgs);
      if (selectedIds.length > 0) getPixelsBulk(selectedIds).then(r => setPixels(r.data)).catch(() => {});
    }
    if (next === 4) {
      selectedIds.forEach(id => {
        const cfg = configs[id];
        if (!cfg) return;
        const firstLoc = cfg.locations?.[0];
        updateAccountSettings(id, {
          location_key: firstLoc?.key || '',
          location: firstLoc?.name || '',
          radius_km: firstLoc?.radius_km || 30,
          locations: (cfg.locations || []).filter(l => l.key).map(l => ({
            key: l.key!, name: l.name || '', radius_km: l.radius_km,
          })),
          gender: cfg.gender || 0,
          pixel_id: cfg.pixel_id || '',
          page_id: cfg.page_id || '',
          budget_euro: cfg.budget_euro || 50,
          impressum_url: cfg.impressum_url || '',
          // Eingegebene Werte merken – aber NICHT die gespeicherte soka_ad_url
          // mit dem (ggf. kampagnenspezifischen) Webseiten-Link überschreiben.
          ...(cfg.display_url ? { display_url: cfg.display_url } : {}),
          ...(cfg.wordpress_url ? { wordpress_url: cfg.wordpress_url } : {}),
        }).catch(() => {});
      });
    }
    setStep(next);
  };

  return (
    <Layout user={user} onLogout={onLogout} activePage="push" onNavigate={onNavigate}>
      <div style={s.page}>
        {/* Tabs */}
        <div style={s.tabs}>
          <button style={{ ...s.tab, ...(mode === 'new' ? s.tabActive : {}) }}
            onClick={() => { setMode('new'); setStep(1); setPushStarted(false); }}>
            Neue Kampagne starten
          </button>
          <button style={{ ...s.tab, color: '#aeaeb2' }} onClick={() => setMode('copy')}>
            Kampagne kopieren <span style={s.soonBadge}>bald</span>
          </button>
        </div>

        {mode === 'copy' ? (
          <div style={s.comingSoon}>
            <div style={{ fontSize: 42, marginBottom: 16 }}>🔜</div>
            <div style={{ fontSize: 18, fontWeight: 600, color: '#1d1d1f', marginBottom: 8 }}>Kampagne kopieren</div>
            <div style={{ color: '#6e6e73', fontSize: 14 }}>Diese Funktion wird bald verfügbar sein.</div>
          </div>
        ) : (
          <>
            {/* Step indicator */}
            <div style={s.stepIndicator}>
              {STEPS.map((label, i) => {
                const n = i + 1;
                const active = n === step;
                const done = n < step;
                return (
                  <React.Fragment key={n}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5 }}>
                      <div style={{
                        width: 32, height: 32, borderRadius: '50%', display: 'flex',
                        alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700,
                        background: done ? '#34c759' : active ? '#1a3a5c' : '#f0f0f5',
                        color: done || active ? '#fff' : '#aeaeb2',
                        border: `2px solid ${done ? '#34c759' : active ? '#1a3a5c' : '#e8e8ed'}`,
                        transition: 'all 0.2s',
                      }}>
                        {done ? '✓' : n}
                      </div>
                      <span style={{ fontSize: 11, color: active ? '#1a3a5c' : '#aeaeb2', fontWeight: active ? 700 : 400 }}>
                        {label}
                      </span>
                    </div>
                    {i < STEPS.length - 1 && (
                      <div style={{ flex: 1, height: 2, marginBottom: 22, transition: 'background 0.3s',
                        background: n < step ? '#34c759' : '#e8e8ed' }} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>

            {/* Content */}
            <div style={s.stepContent}>
              {step === 1 && <Step1Templates onSelect={t => { setTemplate(t); setSourceCampaignId(t.source_campaign_id || ''); setStep(2); }} />}
              {step === 2 && (loading
                ? <div style={s.emptyState}>Lade Accounts…</div>
                : <Step2Partners selectedIds={selectedIds} onToggle={togglePartner}
                    settings={custSettings} accounts={accounts} />
              )}
              {step === 3 && (
                <Step3Settings selectedIds={selectedIds} accounts={accounts}
                  settings={custSettings} configs={configs} onUpdate={updateConfig}
                  pixels={pixels} />
              )}
              {step === 4 && (
                <Step4Summary selectedIds={selectedIds} accounts={accounts}
                  configs={configs} template={template} mode={mode}
                  sourceCampaignId={sourceCampaignId}
                  onSourceCampaignIdChange={setSourceCampaignId}
                  onStart={() => { setPushStarted(true); setStep(5); }}
                  pushStarted={pushStarted}
                  onCancelPush={() => setPushStarted(false)} />
              )}
              {step === 5 && (
                <Step5Push selectedIds={selectedIds} accounts={accounts}
                  configs={configs} template={template} custSettings={custSettings}
                  sourceCampaignId={sourceCampaignId} />
              )}
            </div>

            {/* Navigation */}
            {step < 5 && (
              <div style={s.nav}>
                <div>{step > 1 && (
                  <button style={s.backBtn} onClick={() => setStep(st => st - 1)}>← Zurück</button>
                )}</div>
                <div style={{ display: 'flex', gap: 10 }}>
                  {step === 2 && (
                    <button style={{ ...s.primaryBtn, opacity: selectedIds.length === 0 ? 0.4 : 1 }}
                      disabled={selectedIds.length === 0} onClick={() => goStep(3)}>
                      Weiter ({selectedIds.length}) →
                    </button>
                  )}
                  {step === 3 && (
                    <button style={s.primaryBtn} onClick={() => goStep(4)}>
                      Zur Zusammenfassung →
                    </button>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const s: Record<string, React.CSSProperties> = {
  page: { padding: '44px 48px 80px' },
  tabs: { display: 'flex', gap: 0, marginBottom: 32, borderBottom: '1px solid #e8e8ed' },
  tab: {
    background: 'transparent', border: 'none', borderBottom: '2px solid transparent',
    padding: '10px 22px', fontSize: 14, fontWeight: 500, cursor: 'pointer',
    color: '#6e6e73', marginBottom: -1, display: 'flex', alignItems: 'center', gap: 8,
  },
  tabActive: { color: '#1a3a5c', fontWeight: 700, borderBottom: '2px solid #1a3a5c' },
  soonBadge: { fontSize: 10, background: '#f5f5f7', color: '#aeaeb2', borderRadius: 4, padding: '2px 6px', fontWeight: 600 },
  comingSoon: { display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '80px 0' },
  stepIndicator: { display: 'flex', alignItems: 'center', marginBottom: 28, padding: '0 10px' },
  stepContent: {
    background: '#fff', border: '1px solid #e8e8ed', borderRadius: 16,
    padding: '28px 32px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)', marginBottom: 20,
  },
  stepHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, flexWrap: 'wrap' as const, gap: 12 },
  stepTitle: { fontSize: 18, fontWeight: 700, color: '#1d1d1f' },
  stepSub: { fontSize: 13, color: '#6e6e73', marginTop: 3 },
  primaryBtn: {
    background: '#1a3a5c', color: '#fff', border: 'none', borderRadius: 10,
    padding: '10px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' as const,
  },
  secondaryBtn: {
    background: '#f5f5f7', color: '#1d1d1f', border: '1px solid #e8e8ed', borderRadius: 10,
    padding: '8px 16px', fontSize: 13, fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap' as const,
  },
  backBtn: {
    background: 'transparent', border: '1px solid #d2d2d7', borderRadius: 10,
    color: '#6e6e73', padding: '10px 18px', fontSize: 13, cursor: 'pointer',
  },
  nav: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  emptyState: { padding: '48px 0', textAlign: 'center' as const, color: '#aeaeb2', fontSize: 14 },
  // Template
  tplGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))', gap: 16 },
  tplCard: {
    background: '#fafafa', border: '1px solid #e8e8ed', borderRadius: 14,
    padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 8,
  },
  tplTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 },
  tplName: { fontSize: 15, fontWeight: 700, color: '#1d1d1f' },
  typeBadge: { fontSize: 10, background: '#e8f0fe', color: '#1a73e8', borderRadius: 5, padding: '2px 7px', fontWeight: 600, whiteSpace: 'nowrap' as const, flexShrink: 0 },
  tplDesc: { fontSize: 12, color: '#6e6e73', lineHeight: 1.5 },
  tplTpl: { fontSize: 11, background: '#f0f0f5', borderRadius: 7, padding: '7px 10px', display: 'flex', flexDirection: 'column', gap: 3 },
  tplMeta: { display: 'flex', justifyContent: 'space-between', fontSize: 12 },
  tplLastUsed: { fontSize: 11, color: '#aeaeb2' },
  tplSelectBtn: { background: '#1a3a5c', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 0', fontSize: 12, fontWeight: 600, cursor: 'pointer', marginTop: 4 },
  menuItem: { display: 'block', width: '100%', textAlign: 'left' as const, background: 'transparent', border: 'none', padding: '9px 16px', fontSize: 13, cursor: 'pointer', color: '#1d1d1f', borderBottom: '1px solid #f5f5f7' },
  // Partner list
  searchInput: { background: '#f5f5f7', border: '1px solid #e8e8ed', borderRadius: 10, padding: '8px 14px', fontSize: 13, color: '#1d1d1f', outline: 'none', width: 240 },
  partnerList: { display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 460, overflowY: 'auto' as const },
  partnerRow: { display: 'flex', alignItems: 'center', padding: '10px 14px', background: '#fafafa', border: '1px solid #e8e8ed', borderRadius: 10, cursor: 'pointer' },
  partnerRowActive: { background: '#f0f5fb', borderColor: '#1a3a5c40' },
  cfgBadge: { fontSize: 10, color: '#aeaeb2', background: '#f0f0f5', borderRadius: 4, padding: '1px 5px' },
  statusDot: { width: 7, height: 7, borderRadius: '50%', flexShrink: 0, display: 'inline-block' },
  // Settings
  card: { background: '#fafafa', border: '1px solid #e8e8ed', borderRadius: 12, overflow: 'hidden' },
  cardHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', background: '#fff' },
  cardBody: { padding: '16px', borderTop: '1px solid #f0f0f5', display: 'flex', flexDirection: 'column', gap: 14 },
  fieldGroup2: { display: 'flex', flexDirection: 'column', gap: 5, flex: 1, minWidth: 0 },
  fieldLabel: { fontSize: 11, fontWeight: 600, color: '#6e6e73' },
  fieldInput: { background: '#fff', border: '1.5px solid #d2d2d7', borderRadius: 8, padding: '7px 10px', fontSize: 13, color: '#1d1d1f', outline: 'none', width: '100%' },
  row2: { display: 'flex', gap: 12, flexWrap: 'wrap' as const },
  row3: { display: 'flex', gap: 10, flexWrap: 'wrap' as const },
  globalRow: { display: 'flex', gap: 14, alignItems: 'flex-end', flexWrap: 'wrap' as const, background: '#f0f5fb', border: '1px solid #c7d2dd', borderRadius: 12, padding: '14px 16px', marginBottom: 16 },
  locDropdown: { position: 'absolute' as const, top: '100%', left: 0, right: 0, zIndex: 100, background: '#fff', border: '1px solid #d2d2d7', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.1)', overflow: 'hidden', marginTop: 2 },
  locOption: { display: 'flex', alignItems: 'center', gap: 6, padding: '9px 12px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid #f5f5f7' },
  // Summary table
  table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: 13 },
  th: { textAlign: 'left' as const, padding: '8px 12px', fontSize: 11, fontWeight: 600, color: '#6e6e73', borderBottom: '2px solid #e8e8ed', whiteSpace: 'nowrap' as const },
  td: { padding: '10px 12px', borderBottom: '1px solid #f0f0f5' },
  // Push
  progressWrap: { background: '#f0f0f5', borderRadius: 8, height: 8, marginBottom: 20, overflow: 'hidden' },
  progressFill: { height: 8, background: 'linear-gradient(90deg, #1a3a5c, #2d6a9f)', borderRadius: 8, transition: 'width 0.4s ease' },
  logBox: { background: '#0d1117', borderRadius: 12, padding: '16px 20px', maxHeight: 380, overflowY: 'auto' as const, fontFamily: 'monospace' },
  logLine: { fontSize: 12, lineHeight: 1.9, whiteSpace: 'pre-wrap' as const },
};

const ms: Record<string, React.CSSProperties> = {
  overlay: { position: 'fixed' as const, inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: '#fff', borderRadius: 18, width: '90%', maxWidth: 680, maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 60px rgba(0,0,0,0.22)' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 24px', borderBottom: '1px solid #e8e8ed', flexShrink: 0 },
  title: { fontSize: 16, fontWeight: 700, color: '#1d1d1f' },
  closeBtn: { background: 'transparent', border: 'none', color: '#aeaeb2', fontSize: 18, cursor: 'pointer', padding: 4 },
  varBar: { display: 'flex', alignItems: 'center', flexWrap: 'wrap' as const, gap: 6, padding: '10px 24px', background: '#fafafa', borderBottom: '1px solid #e8e8ed', flexShrink: 0 },
  varChip: { borderRadius: 6, padding: '3px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer' },
  body: { padding: '20px 24px', overflowY: 'auto' as const, flex: 1, display: 'flex', flexDirection: 'column', gap: 14 },
  row2: { display: 'flex', gap: 14 },
  fieldGroup: { display: 'flex', flexDirection: 'column', gap: 5, flex: 1 },
  label: { fontSize: 11, fontWeight: 600, color: '#6e6e73' },
  input: { background: '#f5f5f7', border: '1.5px solid #e8e8ed', borderRadius: 8, padding: '8px 12px', fontSize: 13, color: '#1d1d1f', outline: 'none', width: '100%' },
  footer: { display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '14px 24px', borderTop: '1px solid #e8e8ed', flexShrink: 0 },
  cancelBtn: { background: '#f5f5f7', border: '1px solid #e8e8ed', borderRadius: 10, padding: '9px 18px', fontSize: 13, cursor: 'pointer', color: '#6e6e73' },
  saveBtn: { background: '#1a3a5c', color: '#fff', border: 'none', borderRadius: 10, padding: '9px 22px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
};
